import { useTracker } from '@archipro-website/tracker';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import {
    trackArticleContactCtaClick,
    trackArticleViewProfileClick,
    type ArticleCtaPlacement,
} from '~/modules/tracking/util/trackArticleProfessionalCta';

interface RevampProfessionalCtasProps {
    professional: { title: string; link: string };
    /** Which module hosts the pair — carried in every click's ExtraData. */
    placement: ArticleCtaPlacement;
    className?: string;
    /** Small pills for compact placements (e.g. under an image figure). */
    compact?: boolean;
}

const pillClass =
    'inline-flex items-center justify-center whitespace-nowrap rounded-full font-sans font-medium leading-[1.3] transition-opacity hover:opacity-80';

const regularPillClass =
    'max-md:h-10 max-md:px-5 max-md:text-sm md:h-[3.25rem] md:px-[1.7225rem] md:text-[1rem]';

const compactPillClass = 'h-8 px-4 text-[0.75rem]';

/**
 * "View Profile" / "Contact" pill pair used by the revamp quote and image
 * modules (Figma 2479:70492). Derived from the article's professional — not
 * page-builder content. Clicks are tracked per placement (BC-4876): View
 * Profile keeps the legacy `ProductProfileButtonClick` series, Contact logs
 * the new `ArticleContactCtaClick`.
 */
export const RevampProfessionalCtas = ({
    professional,
    placement,
    className,
    compact = false,
}: RevampProfessionalCtasProps) => {
    const tracker = useTracker();

    if (!professional.link) return null;

    const sizeClass = compact ? compactPillClass : regularPillClass;

    return (
        <div className={cn('flex items-center gap-[0.625rem]', className)}>
            <RoccoLink
                to={professional.link}
                onClick={() => trackArticleViewProfileClick(tracker, placement)}
                className={cn(
                    pillClass,
                    sizeClass,
                    'bg-[#181818] text-[#f3f3f3] shadow-[0_0.2rem_0.7rem_rgba(0,0,0,0.11)]',
                )}
            >
                View Profile
            </RoccoLink>
            <RoccoLink
                to={`${professional.link}/contact`}
                onClick={() => trackArticleContactCtaClick(tracker, placement)}
                className={cn(
                    pillClass,
                    sizeClass,
                    'border border-solid border-[#181818] text-[#181818]',
                )}
            >
                Contact
            </RoccoLink>
        </div>
    );
};
